import { ReactNode, useEffect, useMemo } from 'react'
import {
  AtendimentoType,
  ContratoType,
  formatDateToPtBr,
  initialQueryState,
  OptionsHelper,
  QueryFilter,
  useDebounce,
  validateDateRange,
  VinculoType,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { RelatorioFilters, useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CheckBox,
  CustomSelect,
  DateInput,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useAuth } from '../../../../../auth'
import { useT } from '_metronic/i18n/Metronici18n'

/** Filtros de select (IOption): o chip mostra o rótulo da opção e o × limpa o campo. */
type SelectFilterKey = Exclude<
  keyof RelatorioFilters,
  'buscarFilhas' | 'range' | 'dataInicial' | 'dataFinal'
>

type Props = {
  /** Destaca o período como obrigatório (ex.: tentou exportar sem período). */
  forceDateRangeError?: boolean
  /** Conteúdo à direita da linha dos campos (ex.: exportar PDF/CSV). */
  actions?: ReactNode
}

export const RelatorioListSearchComponent = ({ forceDateRangeError, actions }: Props) => {
  const { currentUser } = useAuth()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const optionsTiposAtendimento = useMemo(
    () => OptionsHelper.getOptionsTiposAtendimentoUsuario(currentUser),
    [currentUser]
  )

  // Período: no máximo 31 dias; os limites dos inputs acompanham a outra data.
  const { error: dateError, limits: dateLimits } = useMemo(
    () => validateDateRange(filters.dataInicial, filters.dataFinal, { maxRangeDays: 31 }),
    [filters.dataInicial, filters.dataFinal]
  )

  const debouncedFilters = useDebounce(filters, 500)

  // Período incompleto ou inválido fica fora da busca; os demais filtros continuam valendo.
  const search = useMemo(() => {
    const { range, dataInicial, dataFinal } = debouncedFilters
    const { error } = validateDateRange(dataInicial, dataFinal, { maxRangeDays: 31 })

    return [
      debouncedFilters.vinculo ? `responsavel.vinculo:${debouncedFilters.vinculo.value}` : '',
      debouncedFilters.tipoAtendimento ? `tipo:${debouncedFilters.tipoAtendimento.value}` : '',
      debouncedFilters.profissionalInicial
        ? `responsavelInicial.pessoa.id:${debouncedFilters.profissionalInicial.value}`
        : '',
      debouncedFilters.profissionalFinal
        ? `responsavel.pessoa.id:${debouncedFilters.profissionalFinal.value}`
        : '',
      debouncedFilters.estado ? `estado:${debouncedFilters.estado.value}` : '',
      debouncedFilters.solicitante ? `criador.id:${debouncedFilters.solicitante.value}` : '',
      debouncedFilters.especialidade ? `especialidade:${debouncedFilters.especialidade.value}` : '',
      debouncedFilters.contrato
        ? `paciente.empresa.tiposContrato:${debouncedFilters.contrato.value}`
        : '',
      debouncedFilters.unidade && !debouncedFilters.buscarFilhas
        ? `unidade.id:${debouncedFilters.unidade.value}`
        : '',
      debouncedFilters.retorno ? `retorno:${debouncedFilters.retorno.value}` : '',
      !error && range && dataInicial && dataFinal
        ? `${range.value}>${dataInicial},${range.value}<${dataFinal}`
        : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters])

  // Instituição e filhas vão como parâmetros próprios da URL, não no `search`.
  const queryFilters = useMemo((): QueryFilter[] => {
    const queryFilters: QueryFilter[] = []
    if (debouncedFilters.empresa)
      queryFilters.push({
        field: 'empresa',
        operator: '=',
        value: Number(debouncedFilters.empresa.value),
      })
    if (debouncedFilters.buscarFilhas)
      queryFilters.push({ field: 'buscaFilhas', operator: '=', value: true })
    return queryFilters
  }, [debouncedFilters.empresa, debouncedFilters.buscarFilhas])

  useEffect(() => {
    updateState({ ...initialQueryState, search, filters: queryFilters })
  }, [search, queryFilters, updateState])

  // Unidade depende da instituição e não se aplica com as filhas incluídas.
  useEffect(() => {
    if ((!filters.empresa || filters.buscarFilhas) && filters.unidade) {
      setFilter('unidade', undefined)
    }
  }, [filters.empresa, filters.buscarFilhas, filters.unidade, setFilter])

  const empresaParams = useMemo(() => ({ onlyActive: false }), [])
  const unidadeParams = useMemo(
    () => ({ empresaId: Number(filters.empresa?.value) }),
    [filters.empresa?.value]
  )

  // Período começado e incompleto (ou exportação sem período): destaca o que falta.
  const periodoIniciado = !!(
    filters.range ||
    filters.dataInicial ||
    filters.dataFinal ||
    forceDateRangeError
  )
  const periodoCompleto = !!(filters.range && filters.dataInicial && filters.dataFinal)
  const faltando = (campo: unknown) => periodoIniciado && !periodoCompleto && !campo

  // Um erro por data: o que falta preencher tem prioridade sobre o intervalo inválido.
  const erroInicio = faltando(filters.dataInicial)
    ? 'Selecione uma data inicial'
    : dateError.includes('inicial')
    ? dateError
    : undefined
  const erroFim = faltando(filters.dataFinal)
    ? 'Selecione uma data final'
    : dateError && !dateError.includes('inicial')
    ? dateError
    : undefined

  const limparPeriodo = () => {
    setFilter('range', undefined)
    setFilter('dataInicial', '')
    setFilter('dataFinal', '')
  }

  const unidadePlaceholder = !filters.empresa
    ? `Selecione a ${t('empresa.label.lowercase')}`
    : filters.buscarFilhas
    ? 'Indisponível com clientes/contratos'
    : 'Selecione uma unidade'

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo (o período sai inteiro).
  const optionChip = (key: SelectFilterKey, label: string): ActiveFilter | undefined => {
    const option = filters[key]
    return option
      ? { key, label, value: option.label, onRemove: () => setFilter(key, undefined) }
      : undefined
  }

  const activeFilters: ActiveFilter[] = [
    optionChip('empresa', t('empresa.label')),
    filters.buscarFilhas && {
      key: 'buscarFilhas',
      label: 'Clientes/Contratos',
      value: 'Incluídos',
      onRemove: () => setFilter('buscarFilhas', undefined),
    },
    periodoCompleto && {
      key: 'periodo',
      label: filters.range!.label,
      value: `${formatDateToPtBr(filters.dataInicial)} a ${formatDateToPtBr(filters.dataFinal)}`,
      onRemove: limparPeriodo,
    },
    optionChip('unidade', 'Unidade/Setor'),
    optionChip('tipoAtendimento', t('tipoAtendimento.label')),
    optionChip('estado', 'Estado'),
    optionChip('profissionalInicial', 'Profissional iniciou'),
    optionChip('profissionalFinal', 'Profissional finalizou'),
    optionChip('solicitante', 'Solicitante'),
    optionChip('especialidade', t('especialidade.label')),
    optionChip('contrato', 'Serviço contratado'),
    optionChip('retorno', 'Retorno'),
    optionChip('vinculo', 'Vínculo'),
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [
    filters.buscarFilhas,
    filters.unidade,
    filters.tipoAtendimento,
    filters.estado,
    filters.profissionalInicial,
    filters.profissionalFinal,
    filters.solicitante,
    filters.especialidade,
    filters.contrato,
    filters.retorno,
    filters.vinculo,
  ].filter(Boolean).length

  return (
    <TableFilters
      layout='stacked'
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label='Clientes/Contratos'>
            <CheckBox
              id='relatorio-buscar-filhas'
              checked={!!filters.buscarFilhas}
              label='Incluir na busca'
              onChange={() => setFilter('buscarFilhas', filters.buscarFilhas ? undefined : true)}
            />
          </TableFilter>
          <TableFilter label='Unidade/Setor'>
            <CustomSelect
              placeholder={unidadePlaceholder}
              onChange={(unidade?: IOption<number>) => setFilter('unidade', unidade ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={unidadeParams}
              value={filters.unidade}
              isDisabled={!filters.empresa || !!filters.buscarFilhas}
            />
          </TableFilter>
          <TableFilter label={t('tipoAtendimento.label')}>
            <CustomSelect
              placeholder='Selecione um tipo'
              options={optionsTiposAtendimento}
              onChange={(tipo?: IOption<AtendimentoType>) =>
                setFilter('tipoAtendimento', tipo ?? undefined)
              }
              value={filters.tipoAtendimento}
            />
          </TableFilter>
          <TableFilter label='Estado'>
            <CustomSelect
              placeholder='Selecione um estado'
              onChange={(estado?: IOption<number>) => setFilter('estado', estado ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEstadosAtendimento}
              value={filters.estado}
            />
          </TableFilter>
          <TableFilter label='Profissional iniciou'>
            <CustomSelect
              placeholder='Selecione um profissional'
              onChange={(profissional?: IOption<number>) =>
                setFilter('profissionalInicial', profissional ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsProfissionalSaude}
              value={filters.profissionalInicial}
            />
          </TableFilter>
          <TableFilter label='Profissional finalizou'>
            <CustomSelect
              placeholder='Selecione um profissional'
              onChange={(profissional?: IOption<number>) =>
                setFilter('profissionalFinal', profissional ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsProfissionalSaude}
              value={filters.profissionalFinal}
            />
          </TableFilter>
          <TableFilter label='Solicitante'>
            <CustomSelect
              placeholder='Selecione um solicitante'
              onChange={(solicitante?: IOption<number>) =>
                setFilter('solicitante', solicitante ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsSolicitante}
              value={filters.solicitante}
            />
          </TableFilter>
          <TableFilter label={t('especialidade.label')}>
            <CustomSelect
              placeholder='Selecione uma especialidade'
              onChange={(especialidade?: IOption<number>) =>
                setFilter('especialidade', especialidade ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              value={filters.especialidade}
            />
          </TableFilter>
          <TableFilter label='Serviços contratados'>
            <CustomSelect
              placeholder='Selecione um serviço contratado'
              onChange={(contrato?: IOption<ContratoType>) =>
                setFilter('contrato', contrato ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsTiposContrato}
              value={filters.contrato}
            />
          </TableFilter>
          <TableFilter label='Consulta de retorno?'>
            <CustomSelect
              placeholder='Selecione'
              options={OptionsHelper.booleanOptions}
              onChange={(retorno?: IOption<boolean>) => setFilter('retorno', retorno ?? undefined)}
              value={filters.retorno}
            />
          </TableFilter>
          <TableFilter label='Vínculo'>
            <CustomSelect
              placeholder='Selecione um vínculo'
              fetchOptions={OptionsHelper.getOptionsTiposVinculos}
              onChange={(vinculo?: IOption<VinculoType>) =>
                setFilter('vinculo', vinculo ?? undefined)
              }
              value={filters.vinculo}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'atendimento encontrado' : 'atendimentos encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('empresa.label')} wide>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          fetchOptionsParams={empresaParams}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter
        label='Consultas entre'
        error={faltando(filters.range) ? 'Selecione uma opção' : undefined}
      >
        <CustomSelect
          placeholder='Selecione uma opção'
          options={OptionsHelper.consultaDatasOptions}
          onChange={(range?: IOption<string>) => setFilter('range', range ?? undefined)}
          value={filters.range}
        />
      </TableFilter>
      <TableFilter label='Data inicial' error={erroInicio}>
        <DateInput
          value={filters.dataInicial}
          onChange={(e) => setFilter('dataInicial', e.target.value)}
          minDate={dateLimits.startMin}
          maxDate={dateLimits.startMax}
          isInvalid={Boolean(erroInicio)}
        />
      </TableFilter>
      <TableFilter label='Data final' error={erroFim}>
        <DateInput
          value={filters.dataFinal}
          onChange={(e) => setFilter('dataFinal', e.target.value)}
          minDate={dateLimits.endMin}
          maxDate={dateLimits.endMax}
          isInvalid={Boolean(erroFim)}
        />
      </TableFilter>
    </TableFilters>
  )
}
